async function LoadAudioBufferAsync(context: AudioContext, url: string): Promise<AudioBuffer> {
    try {
        const response = await fetch(url);

        if (!response.ok) {
            throw new Error(`Failed to fetch audio file: ${response.status} - ${response.statusText}`);
        }

        const audioData = await response.arrayBuffer();
        return await context.decodeAudioData(audioData);
    } catch (error) {
        console.error(`Error loading audio file from ${url}:`, error);
        throw error;
    }
}

export class SoundSample {
    Buffer: AudioBuffer | null = null;

    SourceNode: AudioBufferSourceNode | null = null;

    Gain: GainNode | null = null;

    Pan: PannerNode | null = null;
    //Pan: StereoPannerNode | null = null;

    bIsPlaying = false;

    static GAudioLocationFolder = "assets/audio/";

    async Init(context: AudioContext, url: string, masterGain: GainNode, initialGain = 1.0) {
        const audioUrl = SoundSample.GAudioLocationFolder + url;

        this.Buffer = await LoadAudioBufferAsync(context, audioUrl);

        this.Gain = context.createGain();
        this.SourceNode = context.createBufferSource();

        this.SourceNode.buffer = this.Buffer;
        this.SourceNode.connect(this.Gain);
        this.Gain.connect(masterGain);
        this.Gain.gain.value = initialGain;
    }

    CreatePanner(audioContext: AudioContext, masterGain: GainNode) {
        if (1) {
            const panner = audioContext.createPanner();
            panner.panningModel = "equalpower"; // "HRTF" for realistic spatialization
            //panner.panningModel = "HRTF"; // "HRTF" for realistic spatialization

            /* panner.distanceModel = "linear"; // No attenuation (distance-independent)
            panner.refDistance = 1; // Base distance for no reduction
            panner.maxDistance = 1; // Fixed maximum distance
            panner.rolloffFactor = 0; // Disable distance attenuation */

            // Disable orientation effects by keeping the panner's orientation neutral
            /* panner.orientationX.setValueAtTime(0, audioContext.currentTime);
            panner.orientationY.setValueAtTime(0, audioContext.currentTime);
            panner.orientationZ.setValueAtTime(-1, audioContext.currentTime); // Default forward direction */

            // Set the position of the sound in 3D space
            panner.positionX.setValueAtTime(0.0, audioContext.currentTime);
            panner.positionY.setValueAtTime(0.0, audioContext.currentTime);
            panner.positionZ.setValueAtTime(0.0, audioContext.currentTime);

            this.Gain?.disconnect();
            this.Gain?.connect(panner);
            panner.connect(masterGain);

            this.Pan = panner;
        } else {
        }
    }

    private InitSourceNode(context: AudioContext) {
        this.SourceNode = context.createBufferSource();
        this.SourceNode.buffer = this.Buffer;
        this.SourceNode.connect(this.Gain!);
    }

    PlayLoopRepeat(context: AudioContext, offset: number, playbackRate: number, delay = 0) {
        if (this.Buffer) {
            if (this.bIsPlaying && this.SourceNode) {
                this.SourceNode.stop();
                this.SourceNode.disconnect();
                //return;
            }
            this.InitSourceNode(context);
            if (playbackRate && this.SourceNode) {
                this.SourceNode.playbackRate.value = Math.max(0.5, playbackRate);
            }
            this.SourceNode!.start(delay > 0.01 ? context.currentTime + delay : 0.0, offset);
            this.bIsPlaying = true;
            this.SourceNode!.onended = () => {
                this.bIsPlaying = false;
            };
        }
    }

    Play(context: AudioContext, bLooping = false, offset = 0.0, startTime = 0.0) {
        if (this.Buffer) {
            if (bLooping) {
                if (!this.bIsPlaying) {
                    this.InitSourceNode(context);
                    /* const offset = Math.random() * this.Buffer.duration;
                    this.SourceNode!.start(0, offset); */
                    this.SourceNode!.start(0, offset);
                    this.bIsPlaying = true;
                    this.SourceNode!.onended = () => {
                        this.bIsPlaying = false;
                    };
                }
            } else {
                if (this.bIsPlaying && this.SourceNode) {
                    this.SourceNode.stop();
                }
                this.InitSourceNode(context);
                this.SourceNode!.start(startTime, offset);
                this.bIsPlaying = true;
                this.SourceNode!.onended = () => {
                    this.bIsPlaying = false;
                };
            }
        }
    }

    Stop() {
        if (this.SourceNode && this.bIsPlaying) {
            this.SourceNode.stop();
            this.bIsPlaying = false;
        }
    }
}

export class GAudioEngine {
    static Instance: GAudioEngine;

    static isSoundEnabled: boolean = false;
    static setSoundEnabled: React.Dispatch<React.SetStateAction<boolean>>;
    static setSoundEnabledFooter: React.Dispatch<React.SetStateAction<boolean>>;

    public Context: AudioContext | null = null;

    public MasterGain: GainNode | null = null;

    //Sources

    private SourceCurrent: AudioBufferSourceNode | null = null;

    constructor() {
        if (window.AudioContext) {
            this.Context = new AudioContext();
            this.MasterGain = this.Context.createGain();
            this.MasterGain.gain.value = GAudioEngine.isSoundEnabled ? 0.75 : 0.0;
            this.MasterGain.connect(this.Context.destination);
        } else {
            console.error("Web Audio API is not supported in this browser");
        }
    }

    ImpulseResponse: AudioBuffer | null = null;
    Convolver: ConvolverNode | null = null;
    ConvolverGain: GainNode | null = null;

    Filter: BiquadFilterNode | null = null;

    CreateImpulseResponse(duration = 2, decay = 2) {
        if (this.Context) {
            const sampleRate = this.Context.sampleRate;
            const length = sampleRate * duration; // Total number of samples
            const impulse = this.Context.createBuffer(2, length, sampleRate); // Stereo
            const left = impulse.getChannelData(0);
            const right = impulse.getChannelData(1);

            for (let i = 0; i < length; i++) {
                const value = (Math.random() * 2 - 1) * Math.pow(1 - i / length, decay);
                left[i] = value;
                right[i] = value; // Identical for stereo
            }

            this.ImpulseResponse = impulse;

            const convolver = this.Context.createConvolver();
            convolver.buffer = impulse; // Assign impulse response
            this.Convolver = convolver;

            // Create a low-pass filter
            if (0) {
                const filter = this.Context.createBiquadFilter();
                filter.type = "lowpass";
                filter.frequency.value = 4000; // Initial cutoff frequency
                filter.Q.value = 4; // Resonance for warmth
                this.Filter = filter;
            } else {
                const filter = this.Context.createBiquadFilter();
                filter.type = "highshelf";
                filter.frequency.value = 4000; // Initial cutoff frequency
                filter.Q.value = 0.5; // Resonance for warmth
                filter.gain.value = -12.0;
                this.Filter = filter;
            }

            //Gain
            this.ConvolverGain = this.Context.createGain();
            this.ConvolverGain.gain.value = 1.0; //2.5;

            if (this.MasterGain) {
                this.Convolver.connect(this.Filter);
                this.Filter.connect(this.ConvolverGain);

                if (1) {
                    //this.ConvolverGain.connect(this.MasterGain);
                } else {
                    // Apply a stereo delay to create width
                    const leftDelay = this.Context.createDelay();
                    const rightDelay = this.Context.createDelay();

                    leftDelay.delayTime.value = 0.02; // Small delay to widen left channel
                    rightDelay.delayTime.value = 0.03; // Small delay to widen right channel

                    // Apply panning to left and right channels for stereo effect
                    const leftPan = this.Context.createStereoPanner();
                    const rightPan = this.Context.createStereoPanner();
                    leftPan.pan.value = -1; // Hard left
                    rightPan.pan.value = 1; // Hard right

                    this.ConvolverGain.connect(leftDelay);
                    this.ConvolverGain.connect(rightDelay);

                    leftDelay.connect(leftPan);
                    rightDelay.connect(rightPan);

                    // Finally, connect the panned and delayed signals to the master gain or destination
                    if (this.MasterGain) {
                        leftPan.connect(this.MasterGain);
                        rightPan.connect(this.MasterGain); // Wet reverb signal goes through biquad filter, delay, and pan
                    }
                }

                //this.Convolver.connect(this.MasterGain);
            }
        }
    }

    CreateAmbientSound() {
        if (this.Context) {
            // Create the first oscillator (base tone)
            const oscillator1 = this.Context.createOscillator();
            oscillator1.type = "sawtooth"; // Base waveform
            oscillator1.frequency.value = 40; // Base frequency in Hz

            // Create the second oscillator (octave higher)
            const oscillator2 = this.Context.createOscillator();
            oscillator2.type = "sawtooth"; // Same waveform as oscillator1
            oscillator2.frequency.value = 90; // One octave higher

            // Create a low-pass filter
            const filter = this.Context.createBiquadFilter();
            filter.type = "lowpass";
            filter.frequency.value = 800; // Initial cutoff frequency
            filter.Q.value = 16; // Resonance for warmth
            this.Filter = filter;

            // Create a gain node to control amplitude
            const gainNode = this.Context.createGain();
            gainNode.gain.value = 0.2; // Initial volume

            // Connect both oscillators to the gain node
            oscillator1.connect(gainNode);
            oscillator2.connect(gainNode);

            // Connect the gain node to the filter, and the filter to the destination
            gainNode.connect(filter);
            filter.connect(this.MasterGain!);
            this.MasterGain!.connect(this.Context.destination);
            //filter.connect(this.Context.destination);

            // Create the first LFO to modulate the filter frequency
            const lfoFilter = this.Context.createOscillator();
            lfoFilter.type = "sine"; // Smooth sine wave for modulation
            lfoFilter.frequency.value = 0.5; // Slow modulation (Hz)

            const lfoFilterGain = this.Context.createGain();
            lfoFilterGain.gain.value = 500; // Modulation depth in Hz

            // Connect the first LFO to the gain, and then to the filter frequency
            lfoFilter.connect(lfoFilterGain);
            //lfoFilterGain.connect(filter.frequency);

            // Create a second LFO to modulate the amplitude of oscillator2 for extra motion
            const lfoOscillator = this.Context.createOscillator();
            lfoOscillator.type = "triangle"; // Triangle wave for a subtle pulsing effect
            lfoOscillator.frequency.value = 0.3; // Very slow modulation (Hz)

            const lfoOscillatorGain = this.Context.createGain();
            lfoOscillatorGain.gain.value = 0.1; // Amplitude modulation depth

            // Connect the second LFO to the amplitude of oscillator2
            lfoOscillator.connect(lfoOscillatorGain);
            lfoOscillatorGain.connect(gainNode.gain);

            // Start all oscillators and LFOs
            oscillator1.start();
            oscillator2.start();
            //lfoFilter.start();
            lfoOscillator.start();
        }
    }

    PlaySoundBuffer(inSoundBuffer: AudioBuffer | null) {
        if (!GAudioEngine.isSoundEnabled) {
            return;
        }

        if (this.Context != null) {
            this.SourceCurrent = this.Context.createBufferSource();
            if (inSoundBuffer) {
                this.SourceCurrent.buffer = inSoundBuffer;
                this.SourceCurrent.connect(this.Context.destination);
                this.SourceCurrent.start();
            }
        }
    }

    toggleSound() {
        GAudioEngine.isSoundEnabled = !GAudioEngine.isSoundEnabled;
        GAudioEngine.setSoundEnabled(GAudioEngine.isSoundEnabled);
        GAudioEngine.setSoundEnabledFooter(GAudioEngine.isSoundEnabled);
        if (GAudioEngine.isSoundEnabled) {
            this.MasterGain!.gain.value = 0.75;
        } else {
            this.MasterGain!.gain.value = 0.0;
        }
    }
}
